import * as React from 'react';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import moment from 'moment';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { EventStatusEnum, EventParticipationStatusEnum, ListMyEventsQuery } from '@graphql';
import { useSession } from '@hooks';

interface BookingSlotProps {
  event: ListMyEventsQuery['myEvents'][0];
}

function EventSlot(props: BookingSlotProps) {
  const { event } = props;
  const { account } = useSession();
  const theme = useTheme();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { t } = useTranslation('event');

  const isOwner = Boolean(account && account.id === event.creator.id);

  const { title, covers } = event;
  const from = event.from ? moment(event.from).format('HH:mm') : null;
  const to = event.to ? moment(event.to).format('HH:mm') : null;
  const subtitle = [from, to].filter(Boolean).join(' - ');
  const avatarUrl = covers?.[0]?.url ?? '';

  const chipLabel = isOwner
    ? t(`status.${event.status}`)
    : t(`participant.status.${event.participants.me?.status ?? EventParticipationStatusEnum.Invited}`);

  const handleClick = () => {
    const search = searchParams.size ? `?${searchParams.toString()}` : '';
    navigate(`/me/calendar/event/${event.id}/detail`, {
      preventScrollReset: true,
      state: {
        from: `${pathname}${search}`,
      },
    });
  };

  return (
    <Stack
      direction="row"
      onClick={handleClick}
      p={0}
      justifyContent="flex-start"
      divider={<Divider />}
      flex={1}
      sx={{
        borderRadius: theme.borderRadius,
        backgroundColor: theme.palette.grey[900],
        overflowY: 'scroll',
      }}
    >
      <Status isOwner={isOwner} status={event.status} participation={event.participants.me} />
      <Stack
        direction="row"
        spacing={2}
        p={1}
        width="100%"
        height="100%"
        alignItems="center"
        justifyContent="space-between"
      >
        <Stack spacing={1} width="100%" justifyContent="space-between" alignItems="center">
          <Stack direction="row" width="100%" spacing={1} alignItems="center">
            <Avatar src={avatarUrl} sx={{ width: 40, height: 40 }}>
              {avatarUrl.length === 0 ? title[0] : ''}
            </Avatar>
            <Stack width="100%">
              <Typography variant="body2" fontWeight="bolder">
                {title}
              </Typography>
              <Stack
                direction="row"
                width="100%"
                spacing={1}
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography variant="caption" fontSize=".8rem" color="textSecondary">
                  {subtitle}
                </Typography>
                <Chip
                  variant="outlined"
                  color="uncolored"
                  size="small"
                  label={chipLabel}
                  sx={{ borderColor: 'gray', color: 'gray' }}
                />
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
}

interface StatusProps {
  isOwner: boolean;
  status: ListMyEventsQuery['myEvents'][0]['status'];
  participation: ListMyEventsQuery['myEvents'][0]['participants']['me'];
}

function Status(props: Readonly<StatusProps>) {
  const { isOwner, status, participation } = props;
  const theme = useTheme();

  const statusColor = {
    [EventStatusEnum.Draft]: theme.palette.background.paper,
    [EventStatusEnum.Published]: theme.palette.success.main,
    [EventStatusEnum.Postponed]: theme.palette.warning.main,
    [EventStatusEnum.Canceled]: theme.palette.error.main,
    [EventStatusEnum.Deleted]: theme.palette.error.main,
    [EventStatusEnum.Removed]: theme.palette.error.main,
  };

  const participantStatusColor = {
    [EventParticipationStatusEnum.Invited]: theme.palette.info.main,
    [EventParticipationStatusEnum.Confirmed]: theme.palette.success.main,
    [EventParticipationStatusEnum.Interested]: theme.palette.warning.main,
    [EventParticipationStatusEnum.Declined]: theme.palette.error.main,
    [EventParticipationStatusEnum.Canceled]: theme.palette.error.main,
    [EventParticipationStatusEnum.Removed]: theme.palette.error.main,
  };

  const color = isOwner
    ? statusColor[status]
    : participantStatusColor[participation?.status ?? EventParticipationStatusEnum.Invited];

  return <Box width="5px" sx={{ backgroundColor: color, borderRadius: '20px' }} />;
}

export default React.memo(EventSlot);
